<script setup lang="ts">
import type { DatetimeFieldOptions, FilterInputEmits, FilterInputProps } from '#layers/data-model/public';
import type { ButtonProps } from '#layers/ui/public';
import type { DatetimeFilterValue } from './filter-value';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { calendarSingleUi, DateTimePickerPanelShell } from '#layers/ui/public';
import {
	buildDatetimeFilterContext,
	calendarDateTimeToDatetimeFilterValue,
	calendarDateToDatetimeFilterValue,
	datetimeFilterNowValue,
	datetimeFilterValueToCalendarDate,
	datetimeFilterValueToCalendarDateTime,
	datetimeFilterValueToTime,
	formatDatetimeFilterValue,
	isDateDatetimeFilter,
	isTimeDatetimeFilter,
	isTimestampValueCompatibleWithBacking,
	timeToDatetimeFilterValue,
} from './filter-value';

interface Props extends FilterInputProps<DatetimeFieldOptions> {
	label?: string;
	size?: ButtonProps['size'];
	/** When false, the clear affordance is hidden (e.g. a required range bound). */
	showClear?: boolean;
}

defineOptions({ name: 'DateTimeFilterValueEditor' });

const props = withDefaults(defineProps<Props>(), {
	size: 'md',
	showClear: true,
});
const emit = defineEmits<FilterInputEmits>();
const { t, locale } = useI18n();

const context = computed(() => buildDatetimeFilterContext(props.primitiveType, props.typeOptions));

const isDate = computed(() => isDateDatetimeFilter(props.primitiveType));
const isTime = computed(() => isTimeDatetimeFilter(props.primitiveType));
const isInteractionDisabled = computed(() => props.disabled || props.readonly);

const displayValue = computed(() => formatDatetimeFilterValue(props.modelValue, context.value, locale.value));
const placeholder = computed(() => (isTime.value ? '' : t('date_range_picker.select_a_date')));
const leadingIcon = computed(() => (isTime.value ? 'lucide:clock' : 'lucide:calendar'));
const canClear = computed(() => props.showClear && props.modelValue != null);

// Each picker branch drives the same model through its own Temporal representation; the guard + emit
// is identical, only the value conversion differs.
function guardedModel<T>(get: () => T | null, toFilterValue: (value: T | null) => DatetimeFilterValue | null) {
	return computed<T | null>({
		get,
		set: (value) => {
			if (isInteractionDisabled.value) return;

			const filterValue = toFilterValue(value);
			if (!isTimestampValueCompatibleWithBacking(filterValue, context.value.primitiveType)) return;

			emit('update:modelValue', filterValue);
		},
	});
}

const dateModel = guardedModel(() => datetimeFilterValueToCalendarDate(props.modelValue), calendarDateToDatetimeFilterValue);
const timeModel = guardedModel(() => datetimeFilterValueToTime(props.modelValue), timeToDatetimeFilterValue);
const dateTimeModel = guardedModel(
	() => datetimeFilterValueToCalendarDateTime(props.modelValue, context.value),
	(value) => calendarDateTimeToDatetimeFilterValue(value, context.value),
);

function setToNow() {
	if (isInteractionDisabled.value) return;

	emit('update:modelValue', datetimeFilterNowValue(context.value));
}

function clearValue() {
	if (isInteractionDisabled.value) return;

	emit('update:modelValue', void 0);
}
</script>

<template>
	<DateTimePickerPanelShell
		:label
		:display-value
		:placeholder
		:leading-icon
		:size
		:disabled
		:readonly
		:can-clear
		input-data-testid="datetime-filter-panel-input"
		clear-data-testid="datetime-filter-clear"
		set-to-now-data-testid="datetime-filter-set-to-now"
		data-testid="datetime-filter-editor"
		@clear="clearValue"
		@set-to-now="setToNow"
	>
		<DCalendar
			v-if="isDate"
			v-model="dateModel"
			:size
			:ui="calendarSingleUi"
			:disabled
			:readonly
			data-testid="datetime-filter-date-editor"
		/>

		<div v-else-if="isTime" class="flex justify-center" data-testid="datetime-filter-time-editor">
			<DInputTime
				v-model="timeModel"
				granularity="minute"
				:size
				:disabled
				:readonly
			/>
		</div>

		<div v-else class="flex flex-col gap-2" data-testid="datetime-filter-datetime-editor">
			<DCalendar
				v-model="dateTimeModel"
				:size
				:ui="calendarSingleUi"
				:disabled
				:readonly
			/>
			<DSeparator />
			<div class="flex justify-center">
				<DInputTime
					v-model="dateTimeModel"
					granularity="minute"
					:size
					:disabled
					:readonly
				/>
			</div>
		</div>
	</DateTimePickerPanelShell>
</template>
